import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { showSuccessSnackbar, showErrorSnackbar } from "../../../redux/slices/snackbar";
import useAuth from "../../../hooks/useAuth";
import { a11yProps } from "../../../utils/common";
import TabPanel from "../../../components/TabPanel";

import {
  Alert as MuiAlert,
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  CardHeader,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import { spacing } from "@mui/system";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, FormProvider } from "react-hook-form";

import SaveCancel from "../../../components/SaveCancel";
import useAdmin from "../../../hooks/useAdmin";
import AdminFields from "./components/AdminFields";
import MenuAclFields from "./components/MenuAclFields";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Card = styled(MuiCard)(spacing);

const Alert = styled(MuiAlert)(spacing);

const useTabStyles = styled({
  root: {
    justifyContent: "center",
    width: "1000px",
  },
  scroller: {
    flexGrow: "0",
  },
});

const validationSchema = Yup.object().shape({
  assignedTo: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    })
    .nullable()
    .required(),
  emailId: Yup.string().required("Required"),
  firstName: Yup.string().required("Required"),
  lastName: Yup.string().required("Required"),
  userName: Yup.string().required("Required"),
  address: Yup.string().required("Required"),
  mobileNo: Yup.string()
    .required("Required")
    .matches(/^\d{10}$/, "Must be a valid phone number with up to 10 digits"),
  status: Yup.string().required("Required"),

  confirmPassword: Yup.string().when("password", {
    is: (val) => (val && val.length > 0 ? true : false),
    then: () =>
      Yup.string()
        .required()
        .oneOf([Yup.ref("password")], "Both password need to be the same"),
  }),
});

function EditAdmin(props) {
  const { getUserMenusWithUserName } = useAuth();
  const [selectedPermissions, setSelectedPermissions] = useState({});
  const [oldUserMenus, setOldUserMenus] = useState([]);
  const dispatch = useDispatch();
  const classes = useTabStyles();
  const { getAdmin, admin, updateAdmin, isSubmitting } = useAdmin();

  React.useEffect(() => {
    if (Object.keys(admin).length !== 0) {
      console.log(admin.status, "admin.status");
      const initialValues = {
        adminId: admin.adminId,
        assignedTo: {
          label: admin.assignedTo?.tenantName,
          value: admin.assignedTo?.tenantId,
        },
        firstName: admin.firstName,
        lastName: admin.lastName,
        status: admin.status,
        address: admin.address,
        mobileNo: admin.mobileNo,
        emailId: admin.emailId,
        userName: admin.userName,
      };
      reset(initialValues);
      fetchUserMenu();
    }
  }, [admin]);

  const methods = useForm({
    resolver: yupResolver(validationSchema),
  });

  const [status] = useState({
    sent: false,
  });
  const { user } = useAuth();

  const { state } = useLocation();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    reset,
    handleSubmit,
    setValue,
    formState: {},
  } = methods;

  useEffect(() => {
    if (user) {
      setValue("assignedTo", {
        label: user.name,
        value: user.tenantId,
      });
    }
  }, [user]);

  const [tabValue, setTabValue] = React.useState(0);
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const onSubmit = async (data) => {
    try {
      data["assignedTo"] = {
        tenantId: data.assignedTo.value,
      };

      let menuAcls = [];
      if (selectedPermissions.length == 0) {
        dispatch(showErrorSnackbar("Please select the menu acl"));
        return;
      }

      for (const index in selectedPermissions) {
        const key = index.split("-");
        menuAcls.push({
          rolePermissionId: Number(key[0]),
          isEnabled: selectedPermissions[index],
        });
      }
      data["menuAcl"] = menuAcls;
      await updateAdmin(data);

      dispatch(showSuccessSnackbar("Admin  updated successfully!"));
      navigate("/cpanel/admin/list");
    } catch (err) {
      console.log(err, "ddddddddddddd");
      dispatch(showErrorSnackbar("Failed to edit admin "));
    }
  };

  const cancelBtn = () => {
    navigate("/cpanel/admin/list");
  };

  const breadCrump = () => {
    return (
      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Typography>Admin</Typography>
        <Typography>Edit</Typography>
      </Breadcrumbs>
    );
  };

  const fetchData = async () => {
    await getAdmin(state.id);
  };

  const fetchUserMenu = async () => {
    const userMenuDetails = await getUserMenusWithUserName(admin.userName);
    console.log(userMenuDetails, "userMenuDetails");
    setOldUserMenus(userMenuDetails);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <React.Fragment>
      <FormProvider {...methods}>
        <Card mb={6}>
          <CardHeader title={t("edit_admin")} subheader={breadCrump()} />
          <CardContent>
            {status && status.sent && (
              <Alert severity="success" my={3}>
                admin updated successfully!
              </Alert>
            )}

            {isSubmitting ? (
              <Box display="flex" justifyContent="center" my={6}>
                <CircularProgress />
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <Tabs
                  classes={{ scroller: classes.scroller }}
                  value={tabValue}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons
                  allowScrollButtonsMobile
                  aria-label="tenant"
                  sx={{
                    justifyContent: "center",
                    width: {
                      xs: "100%", // Full width on mobile devices
                      sm: "500px", // Fixed width on small devices
                      md: "700px", // Wider on medium devices
                      lg: "900px", // Even wider on larger devices
                    },
                    minWidth: "300px", // Ensure a minimum width across all devices
                    maxWidth: "100%", // Ensure it doesn't overflow the container
                  }}
                >
                  {" "}
                  <Tab label="Admin Details" {...a11yProps(0)} />
                  <Tab label="Menu Acl" {...a11yProps(0)} />
                </Tabs>
                <TabPanel value={tabValue} index={0}>
                  <AdminFields fields={validationSchema.fields} />
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                  <MenuAclFields
                    fields={validationSchema.fields}
                    selectedPermissions={selectedPermissions}
                    setSelectedPermissions={setSelectedPermissions}
                    adminUserName={admin?.userName}
                    oldUserMenus={oldUserMenus}
                  />
                </TabPanel>
                <SaveCancel cancelBtn={cancelBtn} resetForm={reset} />
              </form>
            )}
          </CardContent>
        </Card>
      </FormProvider>
    </React.Fragment>
  );
}

export default EditAdmin;
